import type { CustomEventRuntimeEntry, ExecutionContext } from "./types.js";

/**
 * Drains the trigger graph for a custom scene event and returns whether
 * its `eventOutput` terminal was reached. Results are cached on
 * `context.customEventEvalCache` for the current drain pass.
 *
 * @param eventId - Catalog entry id (`SceneCustomEventEntry.id`).
 * @param context - Parent graph execution context (same data snapshot).
 * @returns True when the trigger graph reached `eventOutput`.
 */
export function evaluateCustomEventTrigger(
  eventId: string,
  context: ExecutionContext,
): boolean {
  const cache = context.customEventEvalCache;
  if (cache?.has(eventId)) {
    return cache.get(eventId)!;
  }

  const entry = context.customEventRuntimeEntries?.get(eventId);
  if (!entry) {
    cache?.set(eventId, false);
    return false;
  }

  const fired = drainCustomEventTriggerEntry(entry, context);
  cache?.set(eventId, fired);
  return fired;
}

/**
 * Runs one trigger-graph drain and updates the entry's persistent state.
 */
function drainCustomEventTriggerEntry(
  entry: CustomEventRuntimeEntry,
  context: ExecutionContext,
): boolean {
  const result = entry.runtime.drain(
    context.data,
    context.previousData,
    entry.scheduledNodes,
    entry.previousPatch,
    context.nowMs,
    undefined,
    context.currentNodeContext,
    context.sceneAnimationsByName,
    context.controlRoomSignals,
    context.customEventRuntimeEntries,
    context.functionRuntimeEntries,
    context.variableRuntimeEntries,
    context.variableOverrideApi,
  );

  entry.scheduledNodes = result.scheduledNodes;
  entry.previousPatch = result.patches;

  return Boolean(result.eventOutputReached);
}
